/**
 * Poker bot: pre-flop hand ranking (Chen formula / Omaha heuristics), post-flop Monte-Carlo equity,
 * pot-odds driven calling, value betting and difficulty-tuned bluffing. Uses only its own view.
 */
import { buildDeck, parseCard, type CardId } from '../../core/cards.js';
import { shuffle } from '../../core/rng.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import { bestOfAny, bestOfExact, toFast } from './evaluator.js';
import type { PokerAction, PokerLegal, PokerView } from './engine.js';

/** Chen formula score for two hole cards (≈ -1 .. 20). */
export function chenScore(hole: readonly CardId[]): number {
  const [a, b] = hole.map(parseCard).sort((x, y) => y.rank - x.rank) as [ReturnType<typeof parseCard>, ReturnType<typeof parseCard>];
  const base = (r: number) => (r === 14 ? 10 : r === 13 ? 8 : r === 12 ? 7 : r === 11 ? 6 : r / 2);
  let score = base(a.rank);
  if (a.rank === b.rank) return Math.max(5, score * 2);
  if (a.suit === b.suit) score += 2;
  const gap = a.rank - b.rank - 1;
  score -= gap === 0 ? 0 : gap === 1 ? 1 : gap === 2 ? 2 : gap === 3 ? 4 : 5;
  if (gap <= 1 && a.rank < 12) score += 1;
  return Math.ceil(score);
}

function omahaPreflop(hole: readonly CardId[]): number {
  const cards = hole.map(parseCard);
  let score = 0;
  const ranks = cards.map((c) => c.rank).sort((x, y) => y - x);
  for (let i = 0; i < ranks.length; i++) for (let j = i + 1; j < ranks.length; j++) {
    if (ranks[i] === ranks[j]) score += ranks[i]! >= 10 ? 4 : 2;
    if (Math.abs(ranks[i]! - ranks[j]!) <= 2 && ranks[i] !== ranks[j]) score += 1;
  }
  const suits = new Map<string, number>();
  cards.forEach((c) => suits.set(c.suit, (suits.get(c.suit) ?? 0) + 1));
  for (const n of suits.values()) if (n >= 2) score += 2;
  score += ranks.filter((r) => r >= 11).length;
  return score; // ≈ 0..18
}

/** Monte-Carlo equity against `opponents` random hands. */
export function estimateEquity(view: PokerView, opponents: number, sims: number, rng: BotContext['rng']): number {
  const holeN = view.config.variant === 'PLO' ? 4 : 2;
  const known = new Set([...view.hand, ...view.board]);
  const deck = buildDeck().filter((c) => !known.has(c)).map(toFast);
  const me = view.hand.map(toFast);
  const board = view.board.map(toFast);
  const evalHand = (hole: number[], b: number[]) => (holeN === 4 ? bestOfExact(hole, b, 2) : bestOfAny([...hole, ...b]));
  let wins = 0;
  for (let i = 0; i < sims; i++) {
    shuffle(deck, rng);
    let k = 0;
    const fullBoard = board.slice();
    while (fullBoard.length < 5) fullBoard.push(deck[k++]!);
    const mine = evalHand(me, fullBoard);
    let best = true;
    let ties = 0;
    for (let o = 0; o < opponents; o++) {
      const opp = deck.slice(k, k + holeN);
      k += holeN;
      const v = evalHand(opp, fullBoard);
      if (v > mine) {
        best = false;
        break;
      }
      if (v === mine) ties++;
    }
    if (best) wins += ties ? 1 / (ties + 1) : 1;
  }
  return sims ? wins / sims : 0.5;
}

function opponentsInHand(view: PokerView): number {
  return view.players.filter((p) => p && p.seat !== view.seat && (p.status === 'ACTIVE' || p.status === 'ALLIN')).length;
}

function preflopStrength(view: PokerView): number {
  if (view.config.variant === 'PLO') return Math.min(1, omahaPreflop(view.hand) / 16);
  return Math.min(1, Math.max(0, (chenScore(view.hand) + 1) / 21));
}

function clampRaise(legal: PokerLegal, to: number): number {
  return Math.max(legal.minRaiseTo!, Math.min(legal.maxRaiseTo!, Math.round(to)));
}

export const pokerBot: BotEngine<PokerView, PokerLegal, PokerAction> = {
  decideAction(view, legal, ctx) {
    if (!legal.canFold) return this.handleTimeout(view, legal, ctx);
    if (chance(ctx, ctx.profile.mistakeRate)) {
      if (legal.canCheck) return { type: 'check' };
      return chance(ctx, 0.5) && legal.call !== null ? { type: 'call' } : { type: 'fold' };
    }
    if (this.chooseFold!(view, legal, ctx)) return legal.canCheck ? { type: 'check' } : { type: 'fold' };
    return this.chooseBet!(view, legal, ctx);
  },

  evaluateState(view, ctx) {
    const opps = Math.max(1, opponentsInHand(view));
    if (view.board.length === 0 || ctx.profile.simulations === 0) {
      const pre = preflopStrength(view);
      return Math.pow(pre, 0.6 + opps * 0.25);
    }
    return estimateEquity(view, opps, Math.max(40, ctx.profile.simulations), ctx.rng);
  },

  chooseFold(view, legal, ctx) {
    if (legal.canCheck) return false;
    const equity = this.evaluateState(view, ctx);
    const call = legal.call ?? 0;
    const potOdds = call / (view.pot + call);
    if (chance(ctx, ctx.profile.bluff * 0.5)) return false;
    // Pre-flop: fold weak hands facing raises more readily.
    const margin = view.board.length === 0 ? 0.04 : 0;
    return equity + ctx.profile.aggression * 0.08 < potOdds + margin;
  },

  chooseBet(view, legal, ctx) {
    const equity = this.evaluateState(view, ctx);
    const opps = Math.max(1, opponentsInHand(view));
    const canRaise = legal.minRaiseTo !== null && legal.maxRaiseTo !== null;
    const strongThreshold = 0.5 + 0.3 / opps - ctx.profile.aggression * 0.1;
    if (canRaise && (equity > strongThreshold || chance(ctx, ctx.profile.bluff * (legal.canCheck ? 1 : 0.4)))) {
      const potFactor = equity > 0.8 ? 1 : 0.5 + ctx.rng.next() * 0.4;
      const target = view.currentBet + Math.max(view.blinds.bb, view.pot * potFactor);
      const to = clampRaise(legal, target);
      if (legal.allInTo !== null && to >= legal.allInTo * 0.85) return { type: 'allin' };
      return { type: 'raise', to };
    }
    if (legal.canCheck) return { type: 'check' };
    if (legal.call !== null) return { type: 'call' };
    return { type: 'fold' };
  },

  calculateRisk(view, legal) {
    const me = view.players[view.seat!];
    if (!me || me.chips === 0) return 0;
    return Math.min(1, (legal.maxRaiseTo ?? legal.call ?? 0) / (me.chips + me.bet));
  },

  handleTimeout(_view, legal) {
    return legal.canCheck ? { type: 'check' } : { type: 'fold' };
  },
};
